import React, { useEffect, useMemo, useState } from "react";
import "./LifeAdminView.css";

const CATEGORIES = [
  ["vehicle", "Vehicles"], ["family", "Family"], ["birthday", "Birthdays & gifts"],
  ["pet", "Pets"], ["home", "Home"], ["document", "Documents"],
  ["renewal", "Renewals"], ["subscription", "Subscriptions"],
  ["delivery", "Deliveries"], ["return", "Returns"], ["errand", "Errands"],
];

const emptyForm = {
  category: "renewal", title: "", person_name: "", provider: "", reference: "",
  location: "", details: "", due_at: "", renewal: "none", cost: "", reminder_days: "90,30,7",
};

async function request(path, options = {}) {
  const response = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.detail || "Northstar could not complete that action.");
  }
  return response.status === 204 ? null : response.json();
}

function formatDate(value) {
  if (!value) return "No date set";
  return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function LifeAdminView({ requestConfirmation }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [scanReport, setScanReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [status, setStatus] = useState("Loading Life Admin...");

  const load = async () => {
    try {
      const result = await request("/life-admin");
      setItems(Array.isArray(result) ? result : []);
      setStatus("");
    } catch (error) {
      setStatus(error.message);
    }
  };

  useEffect(() => { load(); }, []);

  const dueSoon = useMemo(() => {
    const now = Date.now();
    const limit = now + 90 * 86400000;
    return items.filter(item => item.due_at && new Date(item.due_at).getTime() <= limit);
  }, [items]);

  const reconnectGoogle = async () => {
    try {
      const result = await request("/integrations/google/connect?services=calendar,mail");
      if (result.url) window.location.href = result.url;
      else setStatus("Northstar could not start Google reconnection.");
    } catch (error) {
      setStatus(error.message);
    }
  };

  const scanSources = async () => {
    setScanning(true);
    setStatus("Checking connected email and calendars...");
    try {
      const result = await request("/life-admin/suggestions");
      const found = Array.isArray(result) ? result : (result.suggestions || []);
      const scanned = result.scanned || {};
      setSuggestions(found);
      setScanReport(scanned);
      const summary = `Checked ${scanned.email_messages || 0} email messages across ${scanned.email_accounts || 0} email account${scanned.email_accounts === 1 ? "" : "s"}, and ${scanned.calendar_events || 0} calendar events.`;
      setStatus(found.length
        ? `${summary} Found ${found.length} item${found.length === 1 ? "" : "s"} for review.`
        : `${summary} Nothing suitable was found.`);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setScanning(false);
    }
  };

  const acceptSuggestion = async suggestion => {
    try {
      const created = await request("/life-admin/suggestions/add", {
        method: "POST",
        body: JSON.stringify(suggestion),
      });
      setItems(previous => [created, ...previous]);
      setSuggestions(previous => previous.filter(item => item.source_key !== suggestion.source_key));
    } catch (error) {
      setStatus(error.message);
    }
  };

  const ignoreSuggestion = async suggestion => {
    try {
      await request("/life-admin/suggestions/ignore", {
        method: "POST",
        body: JSON.stringify({ source_key: suggestion.source_key }),
      });
      setSuggestions(previous => previous.filter(item => item.source_key !== suggestion.source_key));
    } catch (error) {
      setStatus(error.message);
    }
  };

  const submit = async event => {
    event.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const created = await request("/life-admin", {
        method: "POST",
        body: JSON.stringify({ ...form, title: form.title.trim(), due_at: form.due_at || null }),
      });
      setItems(previous => [created, ...previous]);
      setForm(emptyForm);
      setStatus("Saved.");
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSaving(false);
    }
  };

  const renew = async item => {
    try {
      const updated = await request(`/life-admin/${item.id}/renew`, { method: "POST" });
      setItems(previous => updated.status === "active"
        ? previous.map(candidate => candidate.id === item.id ? updated : candidate)
        : previous.filter(candidate => candidate.id !== item.id));
    } catch (error) {
      setStatus(error.message);
    }
  };

  const archive = async item => {
    const approved = requestConfirmation
      ? await requestConfirmation({ title: "Archive this item?", message: item.title, confirmLabel: "Archive" })
      : false;
    if (!approved) return;
    try {
      await request(`/life-admin/${item.id}`, { method: "DELETE" });
      setItems(previous => previous.filter(candidate => candidate.id !== item.id));
    } catch (error) {
      setStatus(error.message);
    }
  };

  return (
    <div className="ns-life-admin-view">
      <header className="ns-life-admin-header">
        <div><span>Household and family</span><h2>Life Admin</h2><p>Renewals, documents, vehicles, pets, deliveries and errands in one place.</p></div>
        <strong>{items.length} active</strong>
      </header>

      <section className="ns-life-scan">
        <div><h3>Find things Northstar can track</h3><p>Review connected email for renewals, deliveries, returns and warranties, plus calendars for birthdays and anniversaries. Nothing is added without your approval.</p></div>
        <button type="button" onClick={scanSources} disabled={scanning}>{scanning ? "Scanning..." : "Scan email & calendars"}</button>
      </section>

      {scanReport && (
        <section className="ns-life-scan-report" aria-live="polite">
          <h3>Last scan</h3>
          <div>
            <span><strong>{scanReport.email_accounts || 0}</strong><small>email accounts</small></span>
            <span><strong>{scanReport.email_messages || 0}</strong><small>matching emails opened</small></span>
            <span><strong>{scanReport.calendar_events || 0}</strong><small>calendar events checked</small></span>
            <span><strong>{scanReport.email_suggestions || 0}</strong><small>email suggestions</small></span>
            <span><strong>{scanReport.calendar_suggestions || 0}</strong><small>calendar suggestions</small></span>
            <span><strong>{((scanReport.duration_ms || 0) / 1000).toFixed(1)}s</strong><small>actual scan time</small></span>
          </div>
          <p>Email search period: {scanReport.lookback_days || 0} days.</p>
          {scanReport.errors?.length > 0 && <details open><summary>{scanReport.errors.length} connection issue{scanReport.errors.length === 1 ? "" : "s"}</summary>{scanReport.errors.map((error, index) => <small key={index}>{error}</small>)}{scanReport.errors.some(error => /invalid_grant|expired|revoked/i.test(error)) && <button type="button" className="ns-life-reconnect" onClick={reconnectGoogle}>Reconnect Google</button>}</details>}
        </section>
      )}

      {suggestions.length > 0 && (
        <section className="ns-life-suggestions">
          <h3>Suggestions to review <span>{suggestions.length}</span></h3>
          <div>{suggestions.map(suggestion => (
            <article key={suggestion.source_key}>
              <div><small>{suggestion.source} · {suggestion.category}</small><strong>{suggestion.title}</strong><p>{suggestion.reason}</p>{suggestion.due_at && <span>Due {formatDate(suggestion.due_at)}</span>}</div>
              <div><button type="button" onClick={() => acceptSuggestion(suggestion)}>Add</button><button type="button" className="secondary" onClick={() => ignoreSuggestion(suggestion)}>Ignore</button></div>
            </article>
          ))}</div>
        </section>
      )}

      {dueSoon.length > 0 && (
        <section className="ns-life-due">
          <h3>Coming up</h3>
          <div>{dueSoon.slice(0, 6).map(item => <span key={item.id}><strong>{item.title}</strong><small>{formatDate(item.due_at)}</small></span>)}</div>
        </section>
      )}

      <form className="ns-life-admin-form" onSubmit={submit}>
        <h3>Add an item</h3>
        <div className="ns-life-admin-fields">
          <label>Type<select value={form.category} onChange={e => setForm(previous => ({ ...previous, category: e.target.value }))}>{CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label className="wide">Title<input value={form.title} onChange={e => setForm(previous => ({ ...previous, title: e.target.value }))} placeholder="e.g. Tesla insurance renewal" required /></label>
          <label>Due or expiry<input type="datetime-local" value={form.due_at} onChange={e => setForm(previous => ({ ...previous, due_at: e.target.value }))} /></label>
          <label>Person<input value={form.person_name} onChange={e => setForm(previous => ({ ...previous, person_name: e.target.value }))} /></label>
          <label>Provider<input value={form.provider} onChange={e => setForm(previous => ({ ...previous, provider: e.target.value }))} /></label>
          <label>Cost<input value={form.cost} onChange={e => setForm(previous => ({ ...previous, cost: e.target.value }))} placeholder="e.g. £35 monthly" /></label>
          <label className="wide">Location<input value={form.location} onChange={e => setForm(previous => ({ ...previous, location: e.target.value }))} placeholder="Useful for errands" /></label>
          <label>Repeats<select value={form.renewal} onChange={e => setForm(previous => ({ ...previous, renewal: e.target.value }))}><option value="none">Does not repeat</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option></select></label>
          <label>Reminder days<input value={form.reminder_days} onChange={e => setForm(previous => ({ ...previous, reminder_days: e.target.value }))} placeholder="90,30,7" /></label>
          <label className="wide">Reference<input value={form.reference} onChange={e => setForm(previous => ({ ...previous, reference: e.target.value }))} placeholder="Policy or document reference—not the document itself" /></label>
          <label className="wide">Notes<textarea value={form.details} onChange={e => setForm(previous => ({ ...previous, details: e.target.value }))} /></label>
        </div>
        <button className="ns-btn primary" type="submit" disabled={saving}>{saving ? "Saving..." : "Add to Life Admin"}</button>
        {status && <p className="ns-life-status">{status}</p>}
      </form>

      {CATEGORIES.map(([category, label]) => {
        const categoryItems = items.filter(item => item.category === category);
        if (!categoryItems.length) return null;
        return (
          <section key={category} className="ns-life-admin-group">
            <h3>{label}<span>{categoryItems.length}</span></h3>
            <div className="ns-life-admin-grid">{categoryItems.map(item => (
              <article key={item.id} className="ns-life-admin-card">
                <div><strong>{item.title}</strong><small>{[item.person_name, item.provider].filter(Boolean).join(" · ") || "Personal administration"}</small></div>
                <dl><div><dt>Due</dt><dd>{formatDate(item.due_at)}</dd></div>{item.location && <div><dt>Location</dt><dd>{item.location}</dd></div>}{item.cost && <div><dt>Cost</dt><dd>{item.cost}</dd></div>}{item.reference && <div><dt>Reference</dt><dd>{item.reference}</dd></div>}</dl>
                {item.details && <p>{item.details}</p>}
                <div className="ns-life-admin-actions"><button type="button" onClick={() => renew(item)}>{item.renewal ? "Renewed" : "Complete"}</button><button type="button" className="secondary" onClick={() => archive(item)}>Archive</button></div>
              </article>
            ))}</div>
          </section>
        );
      })}
    </div>
  );
}


export function LifeAdminToday({ calendar = [], onOpen }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    request("/life-admin").then(result => setItems(Array.isArray(result) ? result : [])).catch(() => {});
  }, []);
  const due = useMemo(() => {
    const now = Date.now();
    const limit = now + 30 * 86400000;
    return items.filter(item => item.due_at && new Date(item.due_at).getTime() <= limit).slice(0, 4);
  }, [items]);
  const nearby = useMemo(() => {
    const calendarPlaces = calendar.map(event => String(event.location || "").toLowerCase()).filter(Boolean);
    return items.filter(item => item.category === "errand" && item.location && calendarPlaces.some(place => {
      const words = String(item.location).toLowerCase().split(/[^a-z0-9]+/).filter(word => word.length > 3);
      return words.some(word => place.includes(word));
    })).slice(0, 3);
  }, [items, calendar]);
  if (!due.length && !nearby.length) return null;
  return (
    <section className="ns-life-today">
      <div><span>Life admin</span><h3>Worth remembering</h3></div>
      {due.map(item => <article key={item.id}><strong>{item.title}</strong><small>{formatDate(item.due_at)}</small></article>)}
      {nearby.map(item => <article key={`nearby-${item.id}`} className="nearby"><strong>While you’re nearby: {item.title}</strong><small>{item.location}</small></article>)}
      <button type="button" onClick={onOpen}>Open Life Admin</button>
    </section>
  );
}
